import { createFileRoute, useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { UpsertTaskForm } from '@/src/components/manage/mutate/upsertTask'
import { getPromptById, upsertPrompt } from '@/src/api/prompt'
import { StatusSwitch } from '@/src/components/ui/status-switch'
import { toast } from 'sonner'
import { Spinner } from '@/src/components/ui/spinner'

export const Route = createFileRoute('/_manage/manage/admin/tasks/$id')({
  component: TaskEditPage,
})

function TaskEditPage() {
  const { id: taskId } = useParams({ from: '/_manage/manage/admin/tasks/$id' })
  const isNew = taskId === 'new'
  const queryClient = useQueryClient()

  const { data: task, isLoading, error } = useQuery({
    queryKey: ['prompt', 'get', taskId],
    queryFn: () => getPromptById({ data: { id: taskId } }),
    enabled: !isNew,
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!task) return
      await upsertPrompt({
        data: {
          ...task,
          enabled,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt', 'get', taskId] })
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
      toast.success('Status updated')
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to update status')
    },
  })

  const handleEnabledChange = (enabled: boolean) => {
    if (isNew) return
    toggleEnabledMutation.mutate(enabled)
  }

  if (isLoading && !isNew) {
    return (
      <div className="bg-background text-foreground">
        <AdminToolbar>
          <AdminToolbar.Left>
            <AdminToolbar.Title>Task</AdminToolbar.Title>
          </AdminToolbar.Left>
        </AdminToolbar>
        <div className="max-w-2xl mx-auto p-6">
          <Spinner size={48} />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title>
            {isNew ? 'Create Task' : 'Edit Task'}
          </AdminToolbar.Title>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          {!isNew && (
            <StatusSwitch
              id="task-enabled"
              label="Enabled"
              checked={task?.enabled ?? true}
              onCheckedChange={handleEnabledChange}
              disabled={isLoading || toggleEnabledMutation.isPending}
            />
          )}
        </AdminToolbar.Right>
      </AdminToolbar>
      <div className="w-full mx-auto">
        {error ? (
          <div className="text-destructive mb-4">{error instanceof Error ? error.message : 'Failed to load task.'}</div>
        ) : (
          <UpsertTaskForm
            id={!isNew ? taskId : undefined}
            isNew={isNew}
            task={task ?? undefined}
            callbackUrl="/manage/admin/tasks"
          />
        )}
      </div>
    </div>
  )
}
